import { useCallback, useEffect, useRef, useState } from 'react';
import type { RiskAssessmentConfig } from '@safetyhub/shared';
import type { BoundingBox } from '../../../lib/inspections';
import { ConfirmDialog } from './ConfirmDialog';
import { getScoreColor } from './hazard-colors';
import { FillVisibleIcon, FillHiddenIcon } from './photo-viewer/icons';

interface HazardRegion {
  id: string;
  /** 발견사항 번호 (1부터 시작) */
  number: number;
  /** 위험 영역 좌표 (정규화된 값 0.0~1.0) */
  boundingBox: BoundingBox;
  /** AI 가능성 점수 (1~10) */
  aiLikelihoodScore?: number | null;
  /** AI 심각도 점수 (1~10) */
  aiSeverityScore?: number | null;
  /** AI 위험도 점수 (1~10, THREE_LEVEL에서 색상 결정용) */
  aiRiskLevel?: number | null;
}

interface InteractiveHazardEditorProps {
  /** 표시할 위험 영역 목록 */
  hazards: HazardRegion[];
  /** 실제 이미지 표시 너비 (px) */
  containerWidth: number;
  /** 실제 이미지 표시 높이 (px) */
  containerHeight: number;
  /** object-contain으로 인한 X 오프셋 (px) */
  offsetX?: number;
  /** object-contain으로 인한 Y 오프셋 (px) */
  offsetY?: number;
  /** 선택된 위험 영역 ID */
  selectedHazardId?: string | null;
  /** 위험 영역 선택 핸들러 */
  onHazardSelect?: (hazardId: string | null) => void;
  /** 위험 영역 변경 핸들러 (드래그 중 실시간 호출) */
  onHazardChange?: (hazardId: string, boundingBox: BoundingBox) => void;
  /** 위험 영역 변경 완료 핸들러 (드래그 종료 시 호출, 서버 저장용) */
  onHazardChangeEnd?: (hazardId: string, boundingBox: BoundingBox) => void;
  /** 위험 영역 삭제 핸들러 */
  onHazardDelete?: (hazardId: string) => void;
  /** 위험성평가 설정 (score 기반 색상 결정용) */
  riskConfig?: RiskAssessmentConfig | null;
}

type DragMode = 'move' | 'resize-tl' | 'resize-tr' | 'resize-bl' | 'resize-br' | null;

interface DragState {
  hazardId: string;
  mode: DragMode;
  startX: number;
  startY: number;
  startBox: BoundingBox;
  /** 드래그 중 현재 boundingBox (즉시 화면 갱신용) */
  currentBox: BoundingBox;
}

/** 롱프레스 대기 중 저장하는 초기 정보 */
interface LongPressState {
  x: number;
  y: number;
  pointerId: number;
  hazardId: string;
  mode: DragMode;
  startBox: BoundingBox;
  target: Element;
}

const LONG_PRESS_DELAY = 300; // ms
const LONG_PRESS_MOVE_THRESHOLD = 5; // px

/**
 * 인터랙티브 위험 영역 에디터
 *
 * - 모바일: 롱프레스(300ms) 후 드래그로 이동/크기 조정
 * - 데스크톱: 즉시 드래그로 이동/크기 조정
 * - 모서리 핸들로 크기 조정
 * - 우상단 X 버튼으로 삭제
 * - 우상단 눈 아이콘으로 채우기 투명도 토글
 */
export function InteractiveHazardEditor({
  hazards,
  containerWidth,
  containerHeight,
  offsetX = 0,
  offsetY = 0,
  selectedHazardId,
  onHazardSelect,
  onHazardChange,
  onHazardChangeEnd,
  onHazardDelete,
  riskConfig,
}: InteractiveHazardEditorProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [fillVisible, setFillVisible] = useState(true);
  /** 롱프레스로 편집 모드 진입한 위험영역 ID (시각적 피드백용) */
  const [editingHazardId, setEditingHazardId] = useState<string | null>(null);

  // 터치 스크롤 방지 (모바일)
  const isDraggingRef = useRef(false);
  const savedBodyStyleRef = useRef<{ overflow: string; overscrollBehavior: string } | null>(null);
  const isActive = hazards.length > 0 && containerWidth > 0 && containerHeight > 0;

  // 롱프레스 타이머 및 초기 상태
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressStateRef = useRef<LongPressState | null>(null);

  // 드래그 중 body scroll lock (overscroll/pull-to-refresh 차단)
  const lockBodyScroll = useCallback(() => {
    savedBodyStyleRef.current = {
      overflow: document.body.style.overflow,
      overscrollBehavior: document.documentElement.style.overscrollBehavior,
    };
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overscrollBehavior = 'none';
  }, []);

  const unlockBodyScroll = useCallback(() => {
    if (savedBodyStyleRef.current) {
      document.body.style.overflow = savedBodyStyleRef.current.overflow;
      document.documentElement.style.overscrollBehavior = savedBodyStyleRef.current.overscrollBehavior;
      savedBodyStyleRef.current = null;
    }
  }, []);

  // 롱프레스 타이머 취소
  const cancelLongPress = useCallback(() => {
    if (longPressTimerRef.current !== null) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    longPressStateRef.current = null;
  }, []);

  // 컴포넌트 언마운트 시 scroll lock 해제 및 타이머 정리
  useEffect(() => {
    return () => {
      unlockBodyScroll();
      cancelLongPress();
    };
  }, [unlockBodyScroll, cancelLongPress]);

  useEffect(() => {
    if (!isActive) return;
    const svg = svgRef.current;
    if (!svg) return;

    // touchmove: 드래그 중 스크롤 방지 (편집 모드 진입 후에만)
    const preventTouchMove = (e: TouchEvent) => {
      if (isDraggingRef.current) {
        e.preventDefault();
      }
    };

    document.addEventListener('touchmove', preventTouchMove, { passive: false });
    return () => {
      document.removeEventListener('touchmove', preventTouchMove);
    };
  }, [isActive]);

  // 삭제 버튼 클릭 핸들러
  const handleDeleteClick = useCallback((hazardId: string) => {
    setPendingDeleteId(hazardId);
    setShowDeleteConfirm(true);
  }, []);

  // 삭제 확인 핸들러
  const handleDeleteConfirm = useCallback(() => {
    if (pendingDeleteId) {
      onHazardDelete?.(pendingDeleteId);
    }
    setShowDeleteConfirm(false);
    setPendingDeleteId(null);
  }, [pendingDeleteId, onHazardDelete]);

  // 삭제 취소 핸들러
  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirm(false);
    setPendingDeleteId(null);
  }, []);

  // 클램핑 유틸
  const clamp = (value: number, min: number, max: number) =>
    Math.max(min, Math.min(max, value));

  // 정규화된 좌표를 픽셀로 변환
  const toPixel = useCallback(
    (normalized: number, dimension: number) => normalized * dimension,
    []
  );

  // 드래그 시작 (롱프레스 완료 또는 마우스 클릭 시 호출)
  const activateDrag = useCallback(
    (
      target: Element,
      pointerId: number,
      clientX: number,
      clientY: number,
      hazardId: string,
      mode: DragMode,
      startBox: BoundingBox,
      isTouch: boolean
    ) => {
      target.setPointerCapture(pointerId);

      isDraggingRef.current = true;
      if (isTouch) lockBodyScroll();

      setEditingHazardId(hazardId);
      setDragState({
        hazardId,
        mode,
        startX: clientX,
        startY: clientY,
        startBox: { ...startBox },
        currentBox: { ...startBox },
      });

      onHazardSelect?.(hazardId);
    },
    [onHazardSelect, lockBodyScroll]
  );

  // pointerDown 핸들러 (rect/circle 공통)
  const handlePointerDown = useCallback(
    (
      e: React.PointerEvent,
      hazardId: string,
      mode: DragMode,
      startBox: BoundingBox
    ) => {
      e.stopPropagation();

      if (e.pointerType === 'touch') {
        // 터치: 롱프레스 타이머 시작 (즉시 드래그 X)
        cancelLongPress(); // 기존 타이머 먼저 취소 (멀티터치 상태 불일치 방지)
        longPressStateRef.current = {
          x: e.clientX,
          y: e.clientY,
          pointerId: e.pointerId,
          hazardId,
          mode,
          startBox,
          target: e.target as Element,
        };

        longPressTimerRef.current = setTimeout(() => {
          const state = longPressStateRef.current;
          if (!state) return;

          // 진동 피드백 (지원하는 기기에서)
          if (navigator.vibrate) {
            navigator.vibrate(50);
          }

          try {
            activateDrag(
              state.target,
              state.pointerId,
              state.x,
              state.y,
              state.hazardId,
              state.mode,
              state.startBox,
              true
            );
          } catch {
            // setPointerCapture 실패 시 선택만
            onHazardSelect?.(state.hazardId);
          }

          longPressTimerRef.current = null;
          longPressStateRef.current = null;
        }, LONG_PRESS_DELAY);
      } else {
        // 마우스: 즉시 드래그 시작 (기존 동작)
        e.preventDefault();
        activateDrag(
          e.target as Element,
          e.pointerId,
          e.clientX,
          e.clientY,
          hazardId,
          mode,
          startBox,
          false
        );
      }
    },
    [activateDrag]
  );

  // 드래그 중
  const handleDragMove = useCallback(
    (e: React.PointerEvent) => {
      // 롱프레스 대기 중에 터치가 이동하면 스크롤 의도로 판단 → 타이머 취소
      if (longPressStateRef.current !== null) {
        const state = longPressStateRef.current;
        const dx = Math.abs(e.clientX - state.x);
        const dy = Math.abs(e.clientY - state.y);
        if (dx > LONG_PRESS_MOVE_THRESHOLD || dy > LONG_PRESS_MOVE_THRESHOLD) {
          cancelLongPress();
        }
        return;
      }

      if (!dragState || !containerWidth || !containerHeight) return;

      const deltaX = (e.clientX - dragState.startX) / containerWidth;
      const deltaY = (e.clientY - dragState.startY) / containerHeight;

      const { startBox, mode, hazardId } = dragState;
      let newBox: BoundingBox;

      if (mode === 'move') {
        // 이동: 전체 박스 이동
        const newX = clamp(startBox.x + deltaX, 0, 1 - startBox.width);
        const newY = clamp(startBox.y + deltaY, 0, 1 - startBox.height);
        newBox = { ...startBox, x: newX, y: newY };
      } else {
        // 리사이즈
        let x = startBox.x;
        let y = startBox.y;
        let width = startBox.width;
        let height = startBox.height;

        const minSize = 0.02; // 최소 크기 2%

        switch (mode) {
          case 'resize-tl':
            // 좌상단: x, y 변경, width, height 반대로 변경
            x = clamp(startBox.x + deltaX, 0, startBox.x + startBox.width - minSize);
            y = clamp(startBox.y + deltaY, 0, startBox.y + startBox.height - minSize);
            width = startBox.x + startBox.width - x;
            height = startBox.y + startBox.height - y;
            break;
          case 'resize-tr':
            // 우상단: y 변경, width 증가, height 반대로 변경
            y = clamp(startBox.y + deltaY, 0, startBox.y + startBox.height - minSize);
            width = clamp(startBox.width + deltaX, minSize, 1 - startBox.x);
            height = startBox.y + startBox.height - y;
            break;
          case 'resize-bl':
            // 좌하단: x 변경, width 반대로 변경, height 증가
            x = clamp(startBox.x + deltaX, 0, startBox.x + startBox.width - minSize);
            width = startBox.x + startBox.width - x;
            height = clamp(startBox.height + deltaY, minSize, 1 - startBox.y);
            break;
          case 'resize-br':
            // 우하단: width, height 증가
            width = clamp(startBox.width + deltaX, minSize, 1 - startBox.x);
            height = clamp(startBox.height + deltaY, minSize, 1 - startBox.y);
            break;
        }

        newBox = { x, y, width, height };
      }

      // 로컬 상태 즉시 업데이트 (화면 즉시 갱신)
      setDragState(prev => prev ? { ...prev, currentBox: newBox } : null);
      // 외부 상태도 업데이트 (저장용)
      onHazardChange?.(hazardId, newBox);
    },
    [dragState, containerWidth, containerHeight, onHazardChange, cancelLongPress]
  );

  // pointerUp/Cancel 핸들러 (SVG 레벨)
  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      // 롱프레스 대기 중 손가락을 떼면 탭 → 위험영역 선택
      if (longPressStateRef.current !== null) {
        const state = longPressStateRef.current;
        cancelLongPress();
        // 탭: 해당 위험영역 선택 (onHazardSelect 호출)
        onHazardSelect?.(state.hazardId);
        return;
      }

      if (dragState) {
        (e.target as Element).releasePointerCapture(e.pointerId);

        // 변경 완료 콜백 호출 (서버 저장용)
        if (onHazardChangeEnd) {
          const { startBox, currentBox } = dragState;

          // boundingBox가 실제로 변경되었는지 확인 (부동소수점 오차 고려)
          const hasChanged =
            Math.abs(currentBox.x - startBox.x) > 0.001 ||
            Math.abs(currentBox.y - startBox.y) > 0.001 ||
            Math.abs(currentBox.width - startBox.width) > 0.001 ||
            Math.abs(currentBox.height - startBox.height) > 0.001;

          if (hasChanged) {
            onHazardChangeEnd(dragState.hazardId, currentBox);
          }
        }

        isDraggingRef.current = false;
        unlockBodyScroll();
        setEditingHazardId(null);
        setDragState(null);
      }
    },
    [dragState, onHazardChangeEnd, unlockBodyScroll, cancelLongPress, onHazardSelect]
  );

  const handlePointerCancel = useCallback(() => {
    cancelLongPress();
    if (dragState) {
      isDraggingRef.current = false;
      unlockBodyScroll();
      setEditingHazardId(null);
      setDragState(null);
    }
  }, [dragState, unlockBodyScroll, cancelLongPress]);

  // 배경 클릭 시 선택 해제 (클릭이 부모로 버블링되지 않도록 stopPropagation)
  const handleBackgroundClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (e.target === svgRef.current) {
        onHazardSelect?.(null);
      }
    },
    [onHazardSelect]
  );

  if (hazards.length === 0 || containerWidth === 0 || containerHeight === 0) {
    return null;
  }

  // 편집 모드 중에만 touchAction: none 적용 (스크롤 차단)
  const isEditingActive = dragState !== null || editingHazardId !== null;

  return (
    <div
      className="absolute inset-0 overflow-hidden"
      style={{ touchAction: isEditingActive ? 'none' : 'auto' }}
    >
      {/* 투명도 토글 버튼 (이미지 우상단) */}
      <button
        type="button"
        onClick={() => setFillVisible((prev) => !prev)}
        className="absolute z-30 flex items-center justify-center w-7 h-7 rounded bg-black/50 hover:bg-black/70 text-white transition-colors"
        style={{ top: offsetY + 6, left: offsetX + containerWidth - 34 }}
        title={fillVisible ? '채우기 숨기기' : '채우기 표시'}
      >
        {fillVisible ? <FillVisibleIcon /> : <FillHiddenIcon />}
      </button>

      <svg
        ref={svgRef}
        width={containerWidth}
        height={containerHeight}
        className="absolute"
        style={{ zIndex: 20, left: offsetX, top: offsetY, touchAction: isEditingActive ? 'none' : 'auto' }}
        onClick={handleBackgroundClick}
        onPointerMove={handleDragMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
      >
        {hazards.map((hazard) => {
          // 드래그 중인 hazard는 로컬 상태의 currentBox 사용 (즉시 화면 갱신)
          const isDragging = dragState?.hazardId === hazard.id;
          const boundingBox = isDragging ? dragState.currentBox : hazard.boundingBox;
          const { x, y, width, height } = boundingBox;

          // 좌표를 0~1 범위로 클램핑
          const clampedX = clamp(x, 0, 1);
          const clampedY = clamp(y, 0, 1);
          const clampedWidth = clamp(width, 0, 1 - clampedX);
          const clampedHeight = clamp(height, 0, 1 - clampedY);

          if (clampedWidth <= 0 || clampedHeight <= 0) {
            return null;
          }

          // 정규화된 좌표를 픽셀로 변환
          const pixelX = toPixel(clampedX, containerWidth);
          const pixelY = toPixel(clampedY, containerHeight);
          const pixelWidth = toPixel(clampedWidth, containerWidth);
          const pixelHeight = toPixel(clampedHeight, containerHeight);

          // score 기반 색상 (N5b: severity 폐기 → score+config 사용)
          const color = getScoreColor(hazard.aiLikelihoodScore, hazard.aiSeverityScore, riskConfig, hazard.aiRiskLevel);
          const isSelected = selectedHazardId === hazard.id;
          const hasSelection = selectedHazardId != null;
          const opacity = hasSelection && !isSelected ? 0.6 : 1;
          const isCurrentlyEditing = editingHazardId === hazard.id;

          // 라벨 크기
          const labelWidth = 24;
          const labelHeight = 20;

          // 핸들 크기
          const handleSize = 10;
          const handleOffset = handleSize / 2 + 2; // 테두리 바깥에 배치

          // 삭제 버튼 크기
          const deleteButtonSize = 20;

          // 박스가 너무 작으면 라벨을 바깥에 배치
          const isBoxTooSmall = pixelWidth < labelWidth + 4 || pixelHeight < labelHeight + 4;
          const labelX = pixelX;
          let labelY = pixelY;

          if (isBoxTooSmall) {
            if (pixelY >= labelHeight + 2) {
              labelY = pixelY - labelHeight - 2;
            } else {
              labelY = pixelY + pixelHeight + 2;
            }
          }

          // 채우기 색상 (투명도 토글 반영)
          const fillColor = fillVisible
            ? (isSelected ? color.selectedFill : color.fill)
            : 'transparent';

          // 롱프레스 편집 모드 진입 시 테두리 강조
          const strokeWidth = isCurrentlyEditing ? 4 : (isSelected ? 3 : 2);
          const strokeColor = isCurrentlyEditing ? '#ffffff' : color.stroke;

          return (
            <g key={hazard.id} opacity={opacity}>
              {/* 위험 영역 사각형 (드래그로 이동) */}
              <rect
                x={pixelX}
                y={pixelY}
                width={pixelWidth}
                height={pixelHeight}
                fill={fillColor}
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                strokeDasharray={isSelected ? undefined : '4,2'}
                rx={4}
                ry={4}
                className="cursor-move"
                onPointerDown={(e) =>
                  handlePointerDown(e, hazard.id, 'move', boundingBox)
                }
              />
              {/* 편집 모드 진입 시 색상 오버레이 (롱프레스 시각 피드백) */}
              {isCurrentlyEditing && (
                <rect
                  x={pixelX}
                  y={pixelY}
                  width={pixelWidth}
                  height={pixelHeight}
                  fill={color.stroke}
                  fillOpacity={0.15}
                  stroke={color.stroke}
                  strokeWidth={4}
                  rx={4}
                  ry={4}
                  className="pointer-events-none"
                />
              )}

              {/* 번호 라벨 배경 */}
              <rect
                x={labelX}
                y={labelY}
                width={labelWidth}
                height={labelHeight}
                fill={color.stroke}
                rx={4}
                ry={4}
                className="pointer-events-none"
              />

              {/* 번호 라벨 텍스트 */}
              <text
                x={labelX + labelWidth / 2}
                y={labelY + labelHeight / 2 + 1}
                fill="white"
                fontSize={11}
                fontWeight="bold"
                textAnchor="middle"
                dominantBaseline="middle"
                className="pointer-events-none select-none"
              >
                #{hazard.number}
              </text>

              {/* 선택된 경우에만 핸들과 삭제 버튼 표시 */}
              {isSelected && (
                <>
                  {/* 리사이즈 핸들 - 좌상단 */}
                  <circle
                    cx={pixelX - handleOffset}
                    cy={pixelY - handleOffset}
                    r={handleSize / 2}
                    fill="white"
                    stroke={color.stroke}
                    strokeWidth={2}
                    className="cursor-nw-resize"
                    onPointerDown={(e) =>
                      handlePointerDown(e, hazard.id, 'resize-tl', boundingBox)
                    }
                  />

                  {/* 리사이즈 핸들 - 우상단 */}
                  <circle
                    cx={pixelX + pixelWidth + handleOffset}
                    cy={pixelY - handleOffset}
                    r={handleSize / 2}
                    fill="white"
                    stroke={color.stroke}
                    strokeWidth={2}
                    className="cursor-ne-resize"
                    onPointerDown={(e) =>
                      handlePointerDown(e, hazard.id, 'resize-tr', boundingBox)
                    }
                  />

                  {/* 리사이즈 핸들 - 좌하단 */}
                  <circle
                    cx={pixelX - handleOffset}
                    cy={pixelY + pixelHeight + handleOffset}
                    r={handleSize / 2}
                    fill="white"
                    stroke={color.stroke}
                    strokeWidth={2}
                    className="cursor-sw-resize"
                    onPointerDown={(e) =>
                      handlePointerDown(e, hazard.id, 'resize-bl', boundingBox)
                    }
                  />

                  {/* 리사이즈 핸들 - 우하단 */}
                  <circle
                    cx={pixelX + pixelWidth + handleOffset}
                    cy={pixelY + pixelHeight + handleOffset}
                    r={handleSize / 2}
                    fill="white"
                    stroke={color.stroke}
                    strokeWidth={2}
                    className="cursor-se-resize"
                    onPointerDown={(e) =>
                      handlePointerDown(e, hazard.id, 'resize-br', boundingBox)
                    }
                  />

                  {/* 삭제 버튼 - 우상단 모서리 중심 */}
                  <g
                    className="cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(hazard.id);
                    }}
                  >
                    <circle
                      cx={pixelX + pixelWidth}
                      cy={pixelY}
                      r={deleteButtonSize / 2}
                      fill="#ef4444"
                      stroke="white"
                      strokeWidth={2}
                    />
                    {/* X 아이콘 */}
                    <line
                      x1={pixelX + pixelWidth - 4}
                      y1={pixelY - 4}
                      x2={pixelX + pixelWidth + 4}
                      y2={pixelY + 4}
                      stroke="white"
                      strokeWidth={2}
                      strokeLinecap="round"
                    />
                    <line
                      x1={pixelX + pixelWidth + 4}
                      y1={pixelY - 4}
                      x2={pixelX + pixelWidth - 4}
                      y2={pixelY + 4}
                      stroke="white"
                      strokeWidth={2}
                      strokeLinecap="round"
                    />
                  </g>
                </>
              )}
            </g>
          );
        })}
      </svg>

      {/* 위험 영역 삭제 확인 모달 */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="위험 영역 삭제"
        message="이 위험 영역을 삭제하시겠습니까?"
        confirmText="삭제"
        confirmStyle="danger"
      />
    </div>
  );
}

export default InteractiveHazardEditor;
